import React from "react";
import styled from "@emotion/styled";
import Tooltip from '@mui/material/Tooltip';
import ErrorIcon from '@mui/icons-material/Error';
import CancelIcon from '@mui/icons-material/Cancel';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { useTheme } from "@mui/styles";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from "react-redux";
import FormatClearIcon from '@mui/icons-material/FormatClear';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SignalWifiBadIcon from '@mui/icons-material/SignalWifiBad';
import WrongLocationIcon from '@mui/icons-material/WrongLocation';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import { selectTeamQueryPerformances } from "../../slices/teamAdminDataSlice";
import { ActionIconWrapper, ColumnContent, ComponentSectionRow, FlexWrapper, Typography } from "../../StyledComponents";

const SweepPerformances = ({ handleOpenViewSweepFailuresModal, origin }) => {
  const theme = useTheme()

  const rows = useSelector(selectTeamQueryPerformances)
  const canOpenFailures = typeof handleOpenViewSweepFailuresModal === "function"
  const reportRows = Array.isArray(rows) ? rows.toReversed() : []

  const renderFailureAction = (failures, failureType, index) => {
    if (!canOpenFailures || !failures || failures.length === 0) {
      return null
    }

    return (
      <ActionIconWrapper
        className={(index%2 === 1 ? 'even ' : '') + 'hover'}
        onClick={() => handleOpenViewSweepFailuresModal(failures, failureType)}
      >
        <OpenInNewIcon style={{height: '1.0rem', width: '1.0rem', color: `${theme.palette.primary.dark}`}} />
      </ActionIconWrapper>
    )
  }

  return (
    <ReportWindow>
      {origin === 'quickStats' &&
        <ReportIntro>
          <IntroTitle>Automated portfolio coverage</IntroTitle>
          <IntroCopy>
            Public record sweeps run monthly for saved properties. Use these summaries to review discrepancy detection trends and confirm mortgages remain linked to their public record sources.
          </IntroCopy>
        </ReportIntro>
      }
      <ReportSurface>
        <ReportSurfaceHeader>
          <div>
            <SurfaceTitle>Sweep summary history</SurfaceTitle>
            <SurfaceMeta>{reportRows.length} monthly summaries</SurfaceMeta>
          </div>
        </ReportSurfaceHeader>
        {reportRows.length === 0 ?
          <EmptyState>No sweep summaries are available yet.</EmptyState>
        :
          <ReportScroll>
        <ComponentSectionRow className={(origin === 'quickStats' ? 'clientView ' : '') + "header recordSweeps"} style={{borderBottom: 'none'}}>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: 'fit-content'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Not Found</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '14/18' : '11', height: '100%', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
              <SignalWifiBadIcon style={{color: `${theme.palette.common.orangeDark}`}} />
            </ColumnContent>
          </Tooltip>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: 'fit-content'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Missing Records</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '18/22' : '12', height: '100%', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
              <WrongLocationIcon style={{color: `${theme.palette.common.orangeDark}`}} />
            </ColumnContent>
          </Tooltip>
          <ColumnContent style={{height: '100%', gridColumn: origin !== 'quickStats' ? '22/26' : '13/17', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, fontWeight: '400', position: 'relative'}}>
            DISCREPANCIES
          </ColumnContent>  
          <ColumnContent style={{height: '100%', gridColumn: origin !== 'quickStats' ? '26/29' : '17/20', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, fontWeight: '400', position: 'relative'}}>
            NEW LEADS
          </ColumnContent>  
          <ColumnContent style={{height: '100%', gridColumn: origin !== 'quickStats' ? '29/32' : '20/23', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, fontWeight: '400', position: 'relative'}}>
            UPDATED LEADS
          </ColumnContent>  
          <ColumnContent style={{height: '100%', gridColumn: origin !== 'quickStats' ? '32' : '23', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, fontWeight: '400', position: 'relative'}}>
            UPGRADED
          </ColumnContent>  
        </ComponentSectionRow>
        <ComponentSectionRow className={(origin === 'quickStats' ? 'clientView ' : '') + "header recordSweeps"}>
          <ColumnContent style={{gridColumn: '1', height: '100%', fontWeight: '400'}}>
            INDEX
          </ColumnContent>
          <ColumnContent style={{gridColumn: '2', height: '100%', fontWeight: '400'}}>
            DATE
          </ColumnContent>
          <ColumnContent style={{gridColumn: '3', height: '100%', fontWeight: '400'}}>
            SELECTION
          </ColumnContent>
          <ColumnContent style={{gridColumn: '4', height: '100%', fontWeight: '400'}}>
            QUERIED
          </ColumnContent>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: 'fit-content'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Successful Queries</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: '5', height: '100%'}}>
              <CheckCircleIcon style={{color: `${theme.palette.primary.main}`}} />
            </ColumnContent>
          </Tooltip>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: 'fit-content'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Failed Queries</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: '6', height: '100%'}}>
              <CancelIcon style={{color: `${theme.palette.common.error}`}} />
            </ColumnContent>
          </Tooltip>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: '5rem'}}>
              <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Inactive</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: '7', height: '100%'}}>
              <ErrorIcon style={{color: `${theme.palette.common.grayDark}`}} />
            </ColumnContent>
          </Tooltip>
          {origin !== 'quickStats' &&
            <>
              <ColumnContent style={{gridColumn: '8', height: '100%', fontWeight: '400'}}>
                PM
              </ColumnContent>
              <ColumnContent style={{gridColumn: '9', height: '100%', fontWeight: '400'}}>
                AT
              </ColumnContent>
              <ColumnContent style={{gridColumn: '10', height: '100%', fontWeight: '400'}}>
                CL
              </ColumnContent>
            </>
          }
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: '10rem'}}>
              <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Formatting Errors</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '11' : '8', height: '100%', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`}}>
              <FormatClearIcon style={{color: `${theme.palette.common.orangeDark}`}} />
            </ColumnContent>
          </Tooltip>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', flexDirection: 'column', justifyContent: "center", width: '9rem'}}>
              <div style={{display: 'flex', justifyContent: 'center', width: '100%'}}>
                <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Improper Query</p>
              </div>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '12' : '9', height: '100%'}}>
              <SpeakerNotesOffIcon style={{color: `${theme.palette.common.orangeDark}`}} />
            </ColumnContent>
          </Tooltip>
          <Tooltip
            componentsProps={{tooltip: {sx: {
              color: `${theme.palette.common.white}`,
              backgroundColor: `${theme.palette.common.black}`,
              fontSize: "2rem",
              margin: ' 0'
            }}}}
            PopperProps={{modifiers: [{
              name: "offset",
              options: {
                offset: [0, -8],
              },
            }]}}
            title={<div style={{display: 'flex', justifyContent: "center", width: '12rem'}}>
              <p style={{fontSize: "1rem", fontWeight: "600", padding: "0 0.5rem", margin: '0'}}>Broken Connection</p>
            </div>} 
            placement="top"
            arrow
          >
            <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '13' : '10', height: '100%'}}>
              <WifiOffIcon style={{color: `${theme.palette.common.orangeDark}`}} />
            </ColumnContent>
          </Tooltip>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '14' : '11', height: '100%', fontWeight: '400', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            <span style={{color: `${theme.palette.common.error}`}}>{origin === 'quickStats' ? 'TOTAL' : 'TOT'}</span>
          </ColumnContent>
          {origin !== 'quickStats' &&
            <>
              <ColumnContent style={{gridColumn: '15', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>PM</span>
              </ColumnContent>
              <ColumnContent style={{gridColumn: '16', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>AT</span>
              </ColumnContent>
              <ColumnContent style={{gridColumn: '17', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>CL</span>
              </ColumnContent>
            </>
          }
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '18' : '12', height: '100%', fontWeight: '400', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            <span style={{color: `${theme.palette.common.error}`}}>{origin === 'quickStats' ? 'TOTAL' : 'TOT'}</span>
          </ColumnContent>
          {origin !== 'quickStats' &&
            <>
              <ColumnContent style={{gridColumn: '19', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>PM</span>
              </ColumnContent>
              <ColumnContent style={{gridColumn: '20', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>AT</span>
              </ColumnContent>
              <ColumnContent style={{gridColumn: '21', height: '100%', fontWeight: '400'}}>
                <span style={{color: `${theme.palette.common.orangeDark}`}}>CL</span>
              </ColumnContent>
            </>
          }
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '22' : '13', height: '100%', fontWeight: '400', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            TOTAL
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '23' : '14', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 1
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '24' : '15', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 2
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '25' : '16', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 3
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '26' : '17', height: '100%', fontWeight: '400', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            TOTAL
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '27' : '18', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 1
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '28' : '19', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 2
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '29' : '20', height: '100%', fontWeight: '400', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            TOTAL
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '30' : '21', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 1
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '31' : '22', height: '100%', fontWeight: '400', fontSize: '0.8rem'}}>
            TIER 2
          </ColumnContent>
          <ColumnContent style={{gridColumn: origin !== 'quickStats' ? '32' : '23', height: '100%', borderLeft: `0.1rem solid ${theme.palette.common.grayText}`, position: 'relative'}}>
            <KeyboardDoubleArrowUpIcon style={{color: `${theme.palette.grayText}`}} />
            TIER 2 to 1
          </ColumnContent>
        </ComponentSectionRow>
        <ReportRows>
          {reportRows.map((row, index) => (
            row.errored ?
              <ComponentSectionRow key={row._id} className={(origin === 'quickStats' ? 'clientView ' : '') + (index%2 === 1 ? 'even ' : '') + 'recordSweeps'}>
                <ColumnContent className="detail" style={{gridColumn: '1', fontSize: '1rem'}}>
                  {index + 1}
                </ColumnContent>
                <ColumnContent className="detail" style={{gridColumn: '2', fontSize: '1rem'}}>
                  {row.date}
                </ColumnContent>
                <ColumnContent className="detail" style={{gridColumn: '3/25'}}>
                  -connection failed-
                </ColumnContent>
              </ComponentSectionRow>
            :
              <Tooltip
                componentsProps={{tooltip: {sx: {
                  color: `${theme.tooltip.caution.color}`,
                  backgroundColor: `${theme.tooltip.caution.background}`,
                  border: `0.1rem solid ${theme.tooltip.caution.border}`,
                  borderBottom: `0.1rem solid ${theme.tooltip.caution.border}`,
                  opacity: '0',
                  borderRadius: '1rem',
                  width: 'fit-content',
                  minWidth: 'fit-content',
                  padding: '1.5rem 2rem',
                }}}}
                PopperProps={{modifiers: [{
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                }]}}
                title={row.monthlyQueriesExhausted ?
                  <FlexWrapper className="column">
                    <Typography className="bold alignTextCenter justifyCenter" style={{width: '33rem'}}>
                      This record sweep exhausted your team's monthly query limit.<br/>
                      Some properties went unverified.<br/>
                      Contact your app admin to request a higher limit.
                    </Typography>
                  </FlexWrapper>
                : null} 
                placement="top"
                arrow
                key={row._id}
              >
                <ComponentSectionRow
                  className={
                    (origin === 'quickStats' ? 
                      'clientView ' : '') 
                    + (index%2 === 1 ? 
                      'evenDark ' : '') 
                    + (row.monthlyQueriesExhausted ? 
                      'caution ' : '') 
                    + 'recordSweeps'
                  }
                >
                  <ColumnContent className="detail" style={{gridColumn: '1', fontSize: '1rem'}}>
                    {index + 1}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '2', fontSize: '1rem'}}>
                    {row.date}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '3', fontSize: '1rem'}}>
                    {row.selection}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '4', fontSize: '1rem'}}>
                    {row.totalQueried}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '5', fontSize: '1rem'}}>
                    {row.totalSuccessfulQueries}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '6', fontSize: '1rem'}}>
                    {row.totalFailedQueries}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: '7', fontSize: '1rem'}}>
                    {row.totalInactive}
                  </ColumnContent>
                  {origin !== 'quickStats' &&
                    <>
                      <ColumnContent 
                        className="detail"
                        style={{
                          gridColumn: '8',
                          fontSize: '1rem',
                          color: (row.propMixLessThanLast || row.propMixGreaterThanLast) ? `${theme.palette.common.white}` : `${theme.text.primary.primary}`,
                          background: row.propMixLessThanLast ? `${theme.accent.error.primary}` : row.propMixGreaterThanLast ? `${theme.accent.success.primary}` : 'none',
                        }}
                      >
                        {row.propMixSuccessfulQueries}
                      </ColumnContent>
                      <ColumnContent className="detail"
                        style={{
                          gridColumn: '9',
                          fontSize: '1rem',
                          color: (row.attomLessThanLast || row.attomGreaterThanLast) ? `${theme.palette.common.white}` : `${theme.text.primary.primary}`,
                          background: row.attomLessThanLast ? `${theme.accent.error.primary}` : row.attomGreaterThanLast ? `${theme.accent.success.primary}` : 'none',
                        }}
                      >
                        {row.attomSuccessfulQueries}
                      </ColumnContent>
                      <ColumnContent className="detail"
                        style={{
                          gridColumn: '10',
                          fontSize: '1rem',
                          color: (row.clCurrentMortgageLessThanLast || row.clCurrentMortgageGreaterThanLast) ? `${theme.palette.common.white}` : `${theme.text.primary.primary}`,
                          background: row.clCurrentMortgageLessThanLast ? `${theme.accent.error.primary}` : row.clCurrentMortgageGreaterThanLast ? `${theme.accent.success.primary}` : 'none'
                        }}
                      >
                        {row.clCurrentMortgageSuccessfulQueries}
                      </ColumnContent>
                    </>
                  }
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '11' : '8', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.formattingErrors.length}
                    {renderFailureAction(row.formattingErrors, 'formattingErrors', index)}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '12' : '9', fontSize: '1rem'}}>
                    {row.improperQueries.length}
                    {renderFailureAction(row.improperQueries, 'improperQueries', index)}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '13' : '10', fontSize: '1rem'}}>
                    {row.brokenConnections.length}
                    {renderFailureAction(row.brokenConnections, 'brokenConnections', index)}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '14' : '11', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.completeNotFound.length}
                    {renderFailureAction(row.completeNotFound, 'failedQueries', index)}
                  </ColumnContent>
                  {origin !== 'quickStats' &&
                    <>
                      <ColumnContent className="detail" style={{gridColumn: '15', fontSize: '1rem'}}>
                        {row.propMixNotFound.length}
                        {renderFailureAction(row.propMixNotFound, 'failedQueries', index)}
                      </ColumnContent>
                      <ColumnContent className="detail" style={{gridColumn: '16', fontSize: '1rem'}}>
                        {row.attomNotFound.length}
                        {renderFailureAction(row.attomNotFound, 'failedQueries', index)}
                      </ColumnContent>
                      <ColumnContent className="detail" style={{gridColumn: '17', fontSize: '1rem'}}>
                        {row.clCurrentMortgageNotFound.length}
                        {renderFailureAction(row.clCurrentMortgageNotFound, 'failedQueries', index)}
                      </ColumnContent>
                    </>
                  }
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '18' : '12', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.completeMissingRecords.length}
                    {renderFailureAction(row.completeMissingRecords, 'failedQueries', index)}
                  </ColumnContent>
                  {origin !== 'quickStats' &&
                    <>
                      <ColumnContent className="detail" style={{gridColumn: '19', fontSize: '1rem'}}>
                        {row.propMixMissingRecords.length}
                        {renderFailureAction(row.propMixMissingRecords, 'failedQueries', index)}
                      </ColumnContent>
                      <ColumnContent className="detail" style={{gridColumn: '20', fontSize: '1rem'}}>
                        {row.attomMissingRecords.length}
                        {renderFailureAction(row.attomMissingRecords, 'failedQueries', index)}
                      </ColumnContent>
                      <ColumnContent className="detail" style={{gridColumn: '21', fontSize: '1rem'}}>
                        {row.clCurrentMortgageMissingRecords.length}
                        {renderFailureAction(row.clCurrentMortgageMissingRecords, 'failedQueries', index)}
                      </ColumnContent>
                    </>
                  }
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '22' : '13', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.totalDiscrepancies}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '23' : '14', fontSize: '1rem'}}>
                    {row.totalTier1Discrepancies}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '24' : '15', fontSize: '1rem'}}>
                    {row.totalTier2Discrepancies}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '25' : '16', fontSize: '1rem'}}>
                    {row.totalTier3Discrepancies}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '26' : '17', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.totalNewLeads}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '27' : '18', fontSize: '1rem'}}>
                    {row.tier1New}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '28' : '19', fontSize: '1rem'}}>
                    {row.tier2New}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '29' : '20', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.totalUpdatedLeads}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '30' : '21', fontSize: '1rem'}}>
                    {row.tier1Updated}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '31' : '22', fontSize: '1rem'}}>
                    {row.tier2Updated}
                  </ColumnContent>
                  <ColumnContent className="detail" style={{gridColumn: origin !== 'quickStats' ? '32' : '23', fontSize: '1rem', borderLeft: `0.1rem solid ${theme.palette.common.black}`}}>
                    {row.tier2Upgraded}
                  </ColumnContent>
                </ComponentSectionRow>
              </Tooltip>
          ))}
        </ReportRows>
          </ReportScroll>
        }
      </ReportSurface>
    </ReportWindow>
  )
}

const ReportWindow = styled.section`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`

const ReportIntro = styled.aside(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 12rem) minmax(0, 1fr);
  gap: 1rem;
  align-items: center;
  padding: 0.8rem 1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.65rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.4rem 1rem rgba(15, 23, 42, 0.06);
`)

const IntroTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 0.92rem;
  font-weight: 900;
  line-height: 1.2;
`)

const IntroCopy = styled.p(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.82rem;
  font-weight: 650;
  line-height: 1.35;
`)

const ReportSurface = styled.section(({ theme }) => `
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.75rem 1.6rem rgba(15, 23, 42, 0.08);
`)

const ReportSurfaceHeader = styled.header(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1rem 0.75rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const SurfaceTitle = styled.div(({ theme }) => `
  color: ${theme.palette.common.black};
  font-size: 0.98rem;
  font-weight: 900;
  line-height: 1.2;
`)

const SurfaceMeta = styled.div(({ theme }) => `
  margin-top: 0.12rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.03rem;
  line-height: 1.2;
  text-transform: uppercase;
`)

const ReportScroll = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  overflow: auto;
  background: ${theme.palette.common.white};

  .recordSweeps {
    min-height: 2.35rem;
    padding: 0;
    border: 0;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorderLight};
    background: ${theme.palette.common.white};
    color: ${theme.palette.common.black};
  }

  .recordSweeps.even,
  .recordSweeps.evenDark {
    background: ${theme.palette.common.grayBorderLight};
  }

  .recordSweeps.caution {
    background: ${theme.palette.common.orangeTransparent};
  }

  .recordSweeps.header {
    min-height: 2.3rem;
    border-top: 0;
    border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
    background: #f1f5f9;
    color: ${theme.palette.common.grayText};
    font-size: 0.72rem;
    font-weight: 900;
    letter-spacing: 0.025rem;
    text-transform: uppercase;
  }

  .recordSweeps > div {
    min-width: 0;
    height: 100% !important;
    min-height: 2.35rem;
    padding: 0.25rem 0.32rem;
    border-left-color: ${theme.palette.common.grayBorder} !important;
    color: inherit;
    font-size: 0.84rem !important;
    font-weight: 750;
    line-height: 1.15;
  }

  .recordSweeps.header > div {
    min-height: 2.3rem;
    font-size: 0.72rem !important;
    font-weight: 900 !important;
  }

  .recordSweeps .detail {
    text-transform: none;
  }

  .recordSweeps svg {
    width: 1.05rem;
    height: 1.05rem;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 999rem;
    border: 0.1rem solid ${theme.palette.common.grayBorder};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    width: 0.45rem;
    height: 0.45rem;
    background-color: ${theme.palette.common.grayBorderLight};
  }
`)

const ReportRows = styled.div(({ theme }) => `
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: fit-content;
  overflow: visible;
  padding: 0;
  background: ${theme.palette.common.white};
`)

const EmptyState = styled.div(({ theme }) => `
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 14rem;
  padding: 2rem;
  color: ${theme.palette.common.grayText};
  font-size: 0.92rem;
  font-weight: 800;
  text-align: center;
`)

export default SweepPerformances
